import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardTitle, CardContent } from "../../../../../../../components/ui/card";
import { AlertCircle, InfoIcon } from "lucide-react";
import { cn } from "../../../../../../../lib/utils";

const days = [
    { key: 'sun', date: '3/30' },
    { key: 'mon', date: '3/31' },
    { key: 'tue', date: '4/1' },
    { key: 'wed', date: '4/2' },
    { key: 'thu', date: '4/3' },
    { key: 'fri', date: '4/4' },
    { key: 'sat', date: '4/5' },
];

const shifts = [
    { id: 'morning', key: 'morning', time: '6am-12pm' },
    { id: 'afternoon', key: 'afternoon', time: '12pm-6pm' },
    { id: 'evening', key: 'evening', time: '6pm-12am' },
    { id: 'night', key: 'night', time: '12am-6am' },
];

// Example coverage data mapping [shiftId][dayIndex]
const coverageData: Record<string, { current: number; total: number }[]> = {
    morning: [
        { current: 3, total: 3 }, { current: 3, total: 3 }, { current: 3, total: 3 },
        { current: 3, total: 3 }, { current: 3, total: 3 }, { current: 3, total: 3 },
        { current: 2, total: 3 },
    ],
    afternoon: [
        { current: 3, total: 3 }, { current: 3, total: 3 }, { current: 3, total: 3 },
        { current: 3, total: 3 }, { current: 3, total: 3 }, { current: 8, total: 5 },
        { current: 3, total: 4 },
    ],
    evening: [
        { current: 2, total: 3 }, { current: 3, total: 3 }, { current: 3, total: 3 },
        { current: 3, total: 3 }, { current: 3, total: 3 }, { current: 3, total: 3 },
        { current: 1, total: 2 },
    ],
    night: [
        { current: 3, total: 3 }, { current: 3, total: 3 }, { current: 3, total: 3 },
        { current: 3, total: 3 }, { current: 3, total: 3 }, { current: 2, total: 1 },
        { current: 0, total: 3 },
    ],
};

function ShiftCoverage() {
    const { t } = useTranslation();

    const getStatusStyles = (current: number, total: number) => {
        if (current < total) return "tw-bg-red-50 tw-text-red-600";
        if (current > total) return "tw-bg-violet-50 tw-text-violet-600";
        return "tw-bg-green-50 tw-text-green-600";
    };

    const counts = useMemo(() => {
        const all = Object.values(coverageData).flat();
        return {
            understaffed: all.filter((data) => data.current < data.total).length,
            overstaffed: all.filter((data) => data.current > data.total).length,
        };
    }, []);

    return (
        <Card className="tw-w-full tw-h-full tw-rounded-xl tw-border-gray-200">
            <CardHeader className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-space-y-0 tw-pb-2">
                <CardTitle className="tw-text-sm tw-font-semibold tw-text-gray-700">{t("shift-coverage")}</CardTitle>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-font-medium tw-bg-red-50 tw-text-red-600">
                        <AlertCircle className="tw-h-3 tw-w-3" /> {counts.understaffed} {t("understaffed")}
                    </span>
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-font-medium tw-bg-violet-50 tw-text-violet-600">
                        <InfoIcon className="tw-h-3 tw-w-3" /> {counts.overstaffed} {t("overstaffed")}
                    </span>
                </div>
            </CardHeader>

            <CardContent>
                <div className="tw-grid tw-grid-cols-[140px_1fr] tw-gap-x-4">
                    <div className="tw-text-xs tw-font-medium tw-text-gray-400 tw-pt-2 tw-pl-2">{t("shift")}</div>

                    <div className="tw-grid tw-grid-cols-7 tw-gap-2 tw-mb-3">
                        {days.map((day) => (
                            <div key={day.key} className="tw-text-center">
                                <div className="tw-text-xs tw-font-semibold tw-text-gray-700">{t(day.key)}</div>
                                <div className="tw-text-[10px] tw-text-gray-400">{day.date}</div>
                            </div>
                        ))}
                    </div>

                    {shifts.map((shift) => (
                        <Fragment key={shift.id}>
                            <div className="tw-flex tw-items-baseline tw-gap-1.5 tw-py-3 tw-pl-2">
                                <span className="tw-text-xs tw-font-semibold tw-text-gray-700">{t(shift.key)}</span>
                                <span className="tw-text-[10px] tw-text-gray-400 tw-whitespace-nowrap">({shift.time})</span>
                            </div>

                            <div className="tw-grid tw-grid-cols-7 tw-gap-2 tw-py-1">
                                {coverageData[shift.id].map((data, idx) => (
                                    <div
                                        key={`${shift.id}-${idx}`}
                                        className={cn(
                                            "tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-2 tw-rounded-md",
                                            getStatusStyles(data.current, data.total)
                                        )}
                                    >
                                        <span className="tw-text-sm tw-font-bold tw-leading-none">{data.current}</span>
                                        <span className="tw-text-[10px] tw-font-medium tw-opacity-70">{t("of")} {data.total}</span>
                                    </div>
                                ))}
                            </div>
                        </Fragment>
                    ))}
                </div>

                <div className="tw-mt-6 tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-text-xs tw-text-gray-500">
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                        <div className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-red-100" />
                        {t("understaffed")}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                        <div className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-green-100" />
                        {t("good-coverage")}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                        <div className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-violet-100" />
                        {t("overstaffed")}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default ShiftCoverage;
